import { BadgePercent, Heart, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer";
import { OptimizedImage } from "@/components/ui/optimized-image";
import coralChaperonePath from "@/assets/generated_images/value-coral-chaperone.png";
import coralChaperoneAvif from "@/assets/generated_images/value-coral-chaperone.avif";
import coralChaperoneWebp from "@/assets/generated_images/value-coral-chaperone.webp";
import navyCaveFriendsPath from "@/assets/generated_images/value-navy-cave-friends.png";
import navyCaveFriendsAvif from "@/assets/generated_images/value-navy-cave-friends.avif";
import navyCaveFriendsWebp from "@/assets/generated_images/value-navy-cave-friends.webp";

// Intrinsic pixel size of both value-prop photos — passed to OptimizedImage so
// the browser reserves the box before decode.
const VALUE_IMAGE_WIDTH = 1280;
const VALUE_IMAGE_HEIGHT = 896;

export function ValueProps() {
  const { t } = useTranslation();

  const valueProps = [
    {
      icon: BadgePercent,
      titleKey: "valueProps.rolesTitle",
      descKey: "valueProps.rolesDesc",
      points: [
        "valueProps.rolesPoint1",
        "valueProps.rolesPoint2",
        "valueProps.rolesPoint3",
        "valueProps.rolesPoint4",
      ],
      bg: "hsl(var(--brand-coral))",
      bgDark: "hsl(var(--brand-coral) / 0.75)",
      accentColor: "hsl(var(--brand-gold))",
      bgHex: "var(--brand-coral)",
      contentSide: "left" as const,
      image: coralChaperonePath,
      imageAvif: coralChaperoneAvif,
      imageWebp: coralChaperoneWebp,
      imageAlt: "Adventure chaperone leading a group hike",
      imagePosition: "center 15%",
      number: "01",
    },
    {
      icon: Heart,
      titleKey: "valueProps.matchTitle",
      descKey: "valueProps.matchDesc",
      points: [
        "valueProps.matchPoint1",
        "valueProps.matchPoint2",
        "valueProps.matchPoint3",
        "valueProps.matchPoint4",
      ],
      bg: "hsl(var(--primary))",
      bgDark: "hsl(var(--primary))",
      accentColor: "hsl(var(--brand-gold))",
      bgHex: "var(--primary)",
      contentSide: "right" as const,
      image: navyCaveFriendsPath,
      imageAvif: navyCaveFriendsAvif,
      imageWebp: navyCaveFriendsWebp,
      imageAlt: "Two friends conquering a cave adventure together",
      imagePosition: "center 55%",
      number: "02",
    },
  ];

  return (
    <section className="overflow-hidden" data-testid="section-value-props">
      {/* Section header */}
      <div className="py-16 md:py-24 px-6">
        <div className="flex items-center justify-center gap-4 mb-6">
          <div className="h-px w-12" style={{ background: "hsl(var(--brand-coral) / 0.4)" }} />
          <span className="text-xs font-bold tracking-[0.3em] uppercase font-sans" style={{ color: "hsl(var(--brand-coral))" }}>
            {t("valueProps.sectionEyebrow", "Why Troopn")}
          </span>
          <div className="h-px w-12" style={{ background: "hsl(var(--brand-coral) / 0.4)" }} />
        </div>
        <h2
          className="text-4xl md:text-5xl lg:text-6xl font-black font-serif tracking-tight text-center"
          data-testid="text-value-props-title"
        >
          {t("valueProps.sectionTitle")}
        </h2>

        {/* Mission manifesto */}
        <div
          className="max-w-2xl mx-auto mt-10 md:mt-14 space-y-6"
          data-testid="value-props-manifesto"
        >
          <p
            className="relative pl-6 py-1 font-serif text-lg md:text-xl leading-relaxed text-foreground/90"
            style={{ borderLeft: "3px solid hsl(var(--brand-gold))" }}
            data-testid="text-mission-quote"
          >
            {t("valueProps.missionQuote")}
          </p>

          <p
            className="text-base md:text-lg leading-relaxed text-muted-foreground"
            data-testid="text-mission-nature"
          >
            {t("valueProps.missionNature")}
          </p>

          <p
            className="text-base md:text-lg leading-relaxed text-muted-foreground"
            data-testid="text-mission-dunbar"
          >
            {t("valueProps.missionDunbar")}
          </p>

          <div
            className="grid grid-cols-4 gap-2 sm:gap-3 pt-1"
            data-testid="dunbar-layers"
          >
            {[
              { num: "5", labelKey: "valueProps.layerCoalition" },
              { num: "15", labelKey: "valueProps.layerKin" },
              { num: "50", labelKey: "valueProps.layerClan" },
              { num: "150", labelKey: "valueProps.layerTroop" },
            ].map((layer) => (
              <div
                key={layer.num}
                className="text-center py-3 px-2 rounded-lg border"
                style={{
                  background: "hsl(var(--primary) / 0.04)",
                  borderColor: "hsl(var(--brand-gold) / 0.35)",
                }}
              >
                <div
                  className="text-2xl md:text-3xl font-black font-serif leading-none"
                  style={{ color: "hsl(var(--primary))" }}
                >
                  {layer.num}
                </div>
                <div className="text-[10px] sm:text-xs font-bold tracking-wider uppercase mt-1.5 text-muted-foreground">
                  {t(layer.labelKey)}
                </div>
              </div>
            ))}
          </div>

          <p
            className="text-base md:text-lg leading-relaxed text-muted-foreground"
            data-testid="text-mission-problem"
          >
            {t("valueProps.missionProblem")}
          </p>

          <p
            className="text-base md:text-lg leading-relaxed font-medium text-foreground"
            data-testid="text-mission-solution"
          >
            {t("valueProps.missionSolution")}
          </p>
        </div>

        {/* Brass divider with diamond ornament */}
        <div
          className="flex items-center justify-center gap-4 mt-12 md:mt-16 max-w-sm mx-auto"
          aria-hidden="true"
        >
          <div
            className="h-px flex-1"
            style={{
              background:
                "linear-gradient(to right, transparent, hsl(var(--brand-gold) / 0.6))",
            }}
          />
          <div
            className="rotate-45 w-2 h-2"
            style={{ background: "hsl(var(--brand-gold))" }}
          />
          <div
            className="h-px flex-1"
            style={{
              background:
                "linear-gradient(to left, transparent, hsl(var(--brand-gold) / 0.6))",
            }}
          />
        </div>
      </div>

      {valueProps.map((prop, index) => (
        <ValueBlock key={index} index={index} prop={prop} />
      ))}
    </section>
  );
}

function ValueBlock({
  index,
  prop,
}: {
  index: number;
  prop: {
    icon: React.ComponentType<{ className?: string }>;
    titleKey: string;
    descKey: string;
    points: string[];
    bg: string;
    bgDark: string;
    accentColor: string;
    bgHex: string;
    contentSide: "left" | "right";
    image: string | undefined;
    imageAvif: string;
    imageWebp: string;
    imageAlt: string;
    imagePosition: string;
    number: string;
  };
}) {
  const { t } = useTranslation();
  const [ref, isVisible] = useIntersectionObserver<HTMLDivElement>({ threshold: 0.1 });
  const Icon = prop.icon;
  const isReversed = prop.contentSide === "right";

  return (
    <div
      ref={ref}
      className={`relative overflow-hidden transition-all duration-1000 ${
        isVisible ? "opacity-100" : "opacity-0"
      }`}
      style={{
        backgroundColor: prop.bg,
        transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
      }}
      data-testid={`card-value-prop-${index}`}
    >
      {/* Grain texture */}
      <div className="absolute inset-0 grain-overlay pointer-events-none" />

      <div
        className={`relative flex flex-col ${
          isReversed ? "lg:flex-row-reverse" : "lg:flex-row"
        } items-stretch min-h-[500px] lg:min-h-[600px]`}
      >
        {/* Text content */}
        <div className={`relative lg:w-[55%] py-16 lg:py-24 px-6 sm:px-10 lg:px-20 flex flex-col justify-center z-10 ${
          isReversed ? "xl:pl-[max(2rem,calc((100vw-80rem)/2+2rem))]" : "xl:pr-[max(2rem,calc((100vw-80rem)/2+2rem))]"
        }`}>
          {/* Giant watermark number — scales down on small screens */}
          <span
            className={`absolute top-4 font-black text-[6rem] sm:text-[9rem] lg:text-[14rem] leading-none select-none pointer-events-none opacity-[0.04] text-white right-4 ${isReversed ? "" : "lg:right-auto lg:left-4"}`}
          >
            {prop.number}
          </span>

          <div className="relative max-w-xl">
            {/* Icon badge */}
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 transition-all duration-700 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
              style={{
                backgroundColor: "hsl(var(--brand-gold) / 0.2)",
                transitionDelay: "0.2s",
              }}
            >
              <Icon className="h-6 w-6 text-white" />
            </div>

            <h3
              className={`text-3xl sm:text-4xl lg:text-5xl font-black font-serif text-white leading-[1.05] mb-5 transition-all duration-700 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: "0.3s" }}
              data-testid={`text-value-prop-title-${index}`}
            >
              {t(prop.titleKey)}
            </h3>

            <p
              className={`text-base lg:text-lg text-white/80 leading-relaxed mb-8 max-w-md transition-all duration-700 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: "0.4s" }}
              data-testid={`text-value-prop-desc-${index}`}
            >
              {t(prop.descKey)}
            </p>

            <ul className="space-y-3.5">
              {prop.points.map((pointKey, idx) => (
                <li
                  key={idx}
                  className={`flex items-start gap-3 text-white transition-all duration-500 ${
                    isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
                  }`}
                  style={{ transitionDelay: `${0.5 + idx * 0.08}s` }}
                >
                  <ArrowRight
                    className="h-4 w-4 shrink-0 mt-1"
                    style={{ color: prop.accentColor }}
                  />
                  <span className="text-sm lg:text-base text-white/85">{t(pointKey)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Image panel */}
        <div className="relative lg:w-[45%] min-h-[280px] sm:min-h-[350px] lg:min-h-0 overflow-hidden">
          {prop.image && (
            <OptimizedImage
              src={prop.image}
              avifSrc={prop.imageAvif}
              webpSrc={prop.imageWebp}
              alt={prop.imageAlt}
              width={VALUE_IMAGE_WIDTH}
              height={VALUE_IMAGE_HEIGHT}
              pictureClassName="absolute inset-0"
              className={`block w-full h-full object-cover transition-all duration-1000 ${
                isVisible ? "scale-100" : "scale-110"
              }`}
              style={{ objectPosition: prop.imagePosition }}
              data-testid={`img-value-prop-${index}`}
            />
          )}
          {/* Color tint overlay */}
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(${isReversed ? "to left" : "to right"}, hsl(${prop.bgHex} / 0.53) 0%, hsl(${prop.bgHex} / 0.15) 35%, transparent 60%)`,
            }}
          />
          {/* Bottom fade for mobile */}
          <div className="absolute inset-0 lg:hidden bg-gradient-to-t from-black/20 to-transparent" />
        </div>
      </div>
    </div>
  );
}
